import { rootstock } from '@/shared/lib/chains';

const TOMBSTONE_TTL_MS = 24 * 60 * 60 * 1000;

type TombstoneStore = Record<string, number>;

export interface DeletedScheduleTombstonesReadResult {
  tombstones: Set<string>;
  storageReadable: boolean;
}

export function getDeletedScheduleTombstoneStorageKey(chainId: number, address: string): string {
  return `bitchill:deleted-schedule-tombstones:${String(chainId)}:${address.toLowerCase()}`;
}

function getLegacyStorageKey(address: string): string {
  return `bitchill:deleted-schedule-tombstones:${address.toLowerCase()}`;
}

function parseStore(raw: string | null): TombstoneStore {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as TombstoneStore;
  } catch {
    return {};
  }
}

function getStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function readStore(
  chainId: number,
  address: string,
): { store: TombstoneStore; storageReadable: boolean } {
  const storage = getStorage();
  if (!storage) return { store: {}, storageReadable: false };

  try {
    const current = parseStore(
      storage.getItem(getDeletedScheduleTombstoneStorageKey(chainId, address)),
    );
    if (chainId !== rootstock.id) return { store: current, storageReadable: true };

    // Production versions before chain scoping used an address-only key.
    const legacyKey = getLegacyStorageKey(address);
    const legacy = parseStore(storage.getItem(legacyKey));
    if (Object.keys(legacy).length === 0) {
      return { store: current, storageReadable: true };
    }
    const migrated = { ...legacy, ...current };
    try {
      storage.setItem(
        getDeletedScheduleTombstoneStorageKey(chainId, address),
        JSON.stringify(migrated),
      );
      storage.removeItem(legacyKey);
    } catch {
      // Migration persistence is best-effort. Keep using the merged in-memory value.
    }
    return { store: migrated, storageReadable: true };
  } catch {
    return { store: {}, storageReadable: false };
  }
}

function writeStore(chainId: number, address: string, store: TombstoneStore): boolean {
  const storage = getStorage();
  if (!storage) return false;

  try {
    if (Object.keys(store).length === 0) {
      storage.removeItem(getDeletedScheduleTombstoneStorageKey(chainId, address));
      return true;
    }

    storage.setItem(
      getDeletedScheduleTombstoneStorageKey(chainId, address),
      JSON.stringify(store),
    );
    return true;
  } catch {
    return false;
  }
}

export function readDeletedScheduleTombstonesResult(
  chainId: number,
  address: string | undefined,
  nowMs = Date.now(),
): DeletedScheduleTombstonesReadResult {
  if (!address) return { tombstones: new Set(), storageReadable: true };

  const { store, storageReadable } = readStore(chainId, address);
  const pruned: TombstoneStore = {};

  for (const [scheduleId, timestamp] of Object.entries(store)) {
    if (typeof timestamp !== 'number') continue;
    if (nowMs - timestamp > TOMBSTONE_TTL_MS) continue;
    pruned[scheduleId.toLowerCase()] = timestamp;
  }

  // Keep storage compact and remove malformed/expired entries.
  if (storageReadable) writeStore(chainId, address, pruned);
  return { tombstones: new Set(Object.keys(pruned)), storageReadable };
}

export function readDeletedScheduleTombstones(
  chainId: number,
  address: string | undefined,
  nowMs = Date.now(),
): Set<string> {
  return readDeletedScheduleTombstonesResult(chainId, address, nowMs).tombstones;
}

export function persistDeletedScheduleTombstone(
  chainId: number,
  address: string | undefined,
  scheduleId: string,
  nowMs = Date.now(),
): boolean {
  if (!address) return false;

  const { store } = readStore(chainId, address);
  store[scheduleId.toLowerCase()] = nowMs;
  return writeStore(chainId, address, store);
}
